import { useState, useRef, useEffect } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/useAuthStore';
import { useAppModeStore, type AppMode } from '../store/useAppModeStore';
import { VERSION } from '../version';
import VehicleTCOTour from './onboarding/VehicleTCOTour';
import FleetGateModal from './FleetGateModal';

// Phase 24: top-nav Personal/Fleet segmented control.
// UI label "Personal" maps to internal enum 'individual'.
function ModeToggle() {
  const activeMode = useAppModeStore((s) => s.activeMode);
  const setMode = useAppModeStore((s) => s.setMode);
  const { user } = useAuthStore();
  const navigate = useNavigate();

  async function handleSelect(next: AppMode) {
    if (next === activeMode) return;
    await setMode(next, !!user);
    // Bounce back to the scenarios list so the page contents reflect the
    // new active mode (the list filters off activeMode in ScenariosPage).
    navigate('/');
  }

  const OPTIONS: { value: AppMode; label: string }[] = [
    { value: 'individual', label: 'Personal' },
    { value: 'fleet', label: 'Fleet' },
  ];

  return (
    <div
      role="tablist"
      aria-label="Mode"
      data-tour="mode-toggle"
      style={{
        display: 'inline-flex',
        background: 'var(--tco-bg)',
        border: '1px solid var(--tco-line)',
        borderRadius: 6,
        padding: 2,
        gap: 0,
      }}
    >
      {OPTIONS.map((opt) => {
        const active = activeMode === opt.value;
        return (
          <button
            key={opt.value}
            type="button"
            role="tab"
            aria-selected={active}
            data-mode={opt.value}
            onClick={() => handleSelect(opt.value)}
            style={{
              padding: '5px 12px',
              fontSize: 12,
              fontWeight: active ? 600 : 500,
              fontFamily: 'inherit',
              background: active ? 'var(--tco-ink)' : 'transparent',
              color: active ? 'var(--tco-bg)' : 'var(--tco-ink-2)',
              border: 'none',
              borderRadius: 4,
              cursor: 'pointer',
              letterSpacing: '0.02em',
              transition: 'background 0.15s, color 0.15s',
            }}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

// Phase 25.5: ? help icon + dropdown in top nav. Left of UserMenu so it sits
// in the auth-adjacent cluster. Two items: "Take a tour" replays the
// onboarding tour for the current mode; "Methodology & Sources" links to
// the static methodology page. Dropdown styling mirrors UserMenu so the
// nav feels visually consistent.
function HelpMenu() {
  const navigate = useNavigate();
  const activeMode = useAppModeStore((s) => s.activeMode);
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  function handleTakeTour() {
    setOpen(false);
    // Defer to next tick so the dropdown unmount finishes before Shepherd
    // attaches its modal overlay (avoids a brief layout flash).
    window.setTimeout(() => {
      window.__vehicletcoTour?.start(activeMode);
    }, 0);
  }

  function handleMethodology() {
    setOpen(false);
    navigate('/methodology');
  }

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen((o) => !o)}
        aria-label="Help"
        title="Help"
        style={{
          display: 'grid',
          placeItems: 'center',
          width: 28,
          height: 28,
          padding: 0,
          background: open ? 'var(--tco-line-soft)' : 'transparent',
          border: '1px solid transparent',
          borderRadius: 999,
          color: open ? 'var(--tco-ink)' : 'var(--tco-ink-2)',
          fontSize: 14,
          fontWeight: 600,
          fontFamily: 'inherit',
          cursor: 'pointer',
          transition: 'background 0.15s, color 0.15s',
        }}
        onMouseEnter={(e) => {
          if (open) return;
          (e.currentTarget as HTMLButtonElement).style.color = 'var(--tco-ink)';
          (e.currentTarget as HTMLButtonElement).style.background = 'var(--tco-line-soft)';
        }}
        onMouseLeave={(e) => {
          if (open) return;
          (e.currentTarget as HTMLButtonElement).style.color = 'var(--tco-ink-2)';
          (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
        }}
      >
        ?
      </button>

      {open && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 6px)',
            right: 0,
            minWidth: 200,
            background: 'var(--tco-surface)',
            border: '1px solid var(--tco-line)',
            borderRadius: 6,
            boxShadow: '0 8px 24px rgba(23,24,26,0.10)',
            zIndex: 100,
            overflow: 'hidden',
          }}
        >
          <button
            onClick={handleTakeTour}
            style={{
              display: 'block',
              width: '100%',
              padding: '10px 14px',
              textAlign: 'left',
              background: 'transparent',
              border: 'none',
              color: 'var(--tco-ink-2)',
              fontSize: 13,
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            Take a tour
          </button>
          <button
            onClick={handleMethodology}
            style={{
              display: 'block',
              width: '100%',
              padding: '10px 14px',
              textAlign: 'left',
              background: 'transparent',
              border: 'none',
              color: 'var(--tco-ink-2)',
              fontSize: 13,
              cursor: 'pointer',
              fontFamily: 'inherit',
              borderTop: '1px solid var(--tco-line)',
            }}
          >
            Methodology &amp; Sources
          </button>
        </div>
      )}
    </div>
  );
}

function UserMenu() {
  const { user, signOut } = useAuthStore();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  async function handleSignOut() {
    setOpen(false);
    await signOut();
    navigate('/login', { replace: true });
  }

  if (!user) {
    return (
      <NavLink
        to="/login"
        style={{
          color: 'var(--tco-ink-2)',
          fontSize: 13,
          textDecoration: 'none',
        }}
      >
        Sign in
      </NavLink>
    );
  }

  const email = user.email ?? '';
  const displayEmail = email.length > 22 ? email.slice(0, 20) + '…' : email;

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen((o) => !o)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          padding: '6px 10px',
          background: 'transparent',
          border: 'none',
          color: 'var(--tco-ink-3)',
          fontSize: 13,
          fontFamily: 'inherit',
          cursor: 'pointer',
          borderRadius: 6,
        }}
      >
        <span>{displayEmail}</span>
        <span style={{ fontSize: 11 }}>▾</span>
      </button>

      {open && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 6px)',
            right: 0,
            minWidth: 160,
            background: 'var(--tco-surface)',
            border: '1px solid var(--tco-line)',
            borderRadius: 6,
            boxShadow: '0 8px 24px rgba(23,24,26,0.10)',
            zIndex: 100,
            overflow: 'hidden',
          }}
        >
          {/* Phase 26 Part 2: Account link surfaces plan + promo code UI. */}
          <button
            onClick={() => {
              setOpen(false);
              navigate('/account');
            }}
            style={{
              display: 'block',
              width: '100%',
              padding: '10px 14px',
              textAlign: 'left',
              background: 'transparent',
              border: 'none',
              borderBottom: '1px solid var(--tco-line)',
              color: 'var(--tco-ink-2)',
              fontSize: 13,
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            Account
          </button>
          <button
            onClick={handleSignOut}
            style={{
              display: 'block',
              width: '100%',
              padding: '10px 14px',
              textAlign: 'left',
              background: 'transparent',
              border: 'none',
              color: 'var(--tco-ink-2)',
              fontSize: 13,
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}

// Phase 24.2: removed the standalone "Compare" nav entry. The /compare route
// (ComparePage) renders an in-memory result that only persists between Run
// Analysis click and a page navigation away — clicking the nav tab in any
// other context showed an empty state. The route is still used by
// ScenarioEditorPage's Run Analysis flow (navigate('/compare')) so it is
// retained in App.tsx.
const NAV_LINKS: {
  to: string;
  label: string;
  end?: boolean;
  activePrefixes?: string[];
  tourId?: string;
}[] = [
  // Phase 24.2: highlight Scenarios while on any /scenario/* editor or
  // results route so the active-state matches the user's mental model.
  { to: '/', label: 'Scenarios', end: true, activePrefixes: ['/scenario/'] },
  { to: '/compare-scenarios', label: 'Scenario Comparison', tourId: 'scenario-diff-tab' },
];

// Phase 24.2: tab-bar with prefix-aware active matching. NavLink's stock
// `end: true` does exact-path matching only, so visiting /scenario/:id left
// every tab inactive. We compute the active state ourselves: a tab is active
// when the current pathname matches `to` exactly OR begins with one of
// `activePrefixes`.
function NavTabs() {
  const { pathname } = useLocation();
  return (
    <nav style={{ display: 'flex', gap: 22 }}>
      {NAV_LINKS.map((l) => {
        const exactMatch = l.end ? pathname === l.to : pathname.startsWith(l.to);
        const prefixMatch = (l.activePrefixes ?? []).some((p) => pathname.startsWith(p));
        const isActive = exactMatch || prefixMatch;
        return (
          <NavLink
            key={l.to}
            to={l.to}
            end={l.end}
            data-active={isActive ? 'true' : 'false'}
            data-tour={l.tourId}
            style={{
              fontSize: 14,
              textDecoration: 'none',
              paddingBottom: 4,
              fontWeight: isActive ? 600 : 400,
              color: isActive ? 'var(--tco-ink)' : 'var(--tco-ink-2)',
              borderBottom: isActive ? '2px solid var(--tco-neg)' : '2px solid transparent',
              transition: 'color 0.15s',
            }}
          >
            {l.label}
          </NavLink>
        );
      })}
    </nav>
  );
}

export default function Layout() {
  // Phase 25: opt the document body into the warm-paper ledger theme. We
  // toggle a class instead of replacing the legacy theme so the (still-dark)
  // login + auth-callback pages render unchanged on /login routes that
  // mount outside Layout.
  useEffect(() => {
    document.body.classList.add('tco-ledger');
    return () => {
      document.body.classList.remove('tco-ledger');
    };
  }, []);

  // Phase 24 mode visual differentiation: tint the header background by the
  // active account mode. Layout only mounts under <ProtectedRoute>, so the
  // unauthenticated /login, /auth/callback, and /methodology routes are
  // unaffected by definition.
  const isFleet = useAppModeStore((s) => s.isFleet);
  const navBg = isFleet ? 'var(--nav-bg-fleet)' : 'var(--nav-bg-personal)';

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', background: 'var(--tco-bg)' }}>
      {/* Header bar — full bleed, hairline border */}
      <header
        style={{
          background: navBg,
          borderBottom: '1px solid var(--tco-line)',
          transition: 'background-color 150ms ease',
        }}
      >
        <div
          style={{
            maxWidth: 1140,
            margin: '0 auto',
            padding: '14px 32px',
            display: 'flex',
            alignItems: 'center',
            gap: 24,
          }}
        >
          {/* TCO mark */}
          <NavLink to="/" style={{ display: 'flex', alignItems: 'center', gap: 14, textDecoration: 'none' }}>
            <span
              style={{
                display: 'grid',
                placeItems: 'center',
                width: 32,
                height: 32,
                borderRadius: 6,
                background: 'var(--tco-ink)',
                color: 'var(--tco-bg)',
                fontWeight: 600,
                fontSize: 12,
                letterSpacing: '0.04em',
              }}
            >
              TCO
            </span>
          </NavLink>

          {/* Tabs */}
          <NavTabs />

          <div style={{ flex: 1 }} />
          <ModeToggle />
          <HelpMenu />
          <UserMenu />
        </div>
      </header>

      {/* Phase 25.5: onboarding tour overlay. Mounted once at the layout
          level so a single Shepherd instance owns the modal/cancel state
          across every authenticated page. */}
      <VehicleTCOTour />

      {/* Phase 26 Part 2: fleet-tier gate modal. Single mount point — any
          surface that calls useAppModeStore.setFleetGateOpen(true) shows
          this modal, and successful redemption flips activeMode to fleet. */}
      <FleetGateModal />

      {/* Page content */}
      <main style={{ flex: 1 }}>
        <Outlet />
      </main>

      {/* Version & data freshness footer */}
      <footer style={{
        padding: '24px 32px',
        textAlign: 'center',
        borderTop: '1px solid var(--tco-line)',
        background: 'var(--tco-surface)',
      }}>
        <p style={{
          margin: 0,
          fontSize: 11,
          color: 'var(--tco-ink-3)',
          letterSpacing: '0.02em',
        }}>
          {VERSION} · MSRP data: March 2026 · Depreciation: Argonne ANL/ESD-22/2 + iSeeCars 2026 · Fuel &amp; electricity prices: live via EIA
        </p>
        <p style={{
          margin: '6px 0 0',
          fontSize: 11,
          color: 'var(--tco-ink-3)',
          letterSpacing: '0.02em',
        }}>
          <NavLink
            to="/methodology"
            style={{
              color: 'var(--tco-ink-3)',
              textDecoration: 'none',
              borderBottom: '1px solid var(--tco-line)',
              paddingBottom: 1,
              fontFamily: 'inherit',
            }}
          >
            Methodology &amp; Sources
          </NavLink>
          {' · '}
          <NavLink
            to="/works-cited"
            style={{
              color: 'var(--tco-ink-3)',
              textDecoration: 'none',
              borderBottom: '1px solid var(--tco-line)',
              paddingBottom: 1,
              fontFamily: 'inherit',
            }}
          >
            Works Cited
          </NavLink>
        </p>
      </footer>
    </div>
  );
}
